import { useQuery } from '@tanstack/react-query';
import { HeartPulse, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { ComponentHealth } from '@/api/types';
import { cn } from '@/lib/cn';
import { ago } from '@/lib/format';

const STATUS_CFG: Record<string, { tone: string; bg: string; icon: typeof CheckCircle2; label: string }> = {
  UP:       { tone: 'text-kam-green', bg: 'bg-kam-green-soft border-kam-green/30', icon: CheckCircle2, label: 'Healthy' },
  DEGRADED: { tone: 'text-kam-amber', bg: 'bg-kam-amber-soft border-kam-amber/30', icon: AlertTriangle, label: 'Degraded' },
  DOWN:     { tone: 'text-kam-red',   bg: 'bg-kam-red-soft border-kam-red/30',     icon: XCircle,       label: 'Down' },
};

export function SystemHealth() {
  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: dashboardApi.systemHealth,
    refetchInterval: 10_000,
  });

  const overall = data ? STATUS_CFG[data.status] : STATUS_CFG.UP;
  const OverallIcon = overall.icon;

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <HeartPulse size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">System Health</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'checking…' : data
              ? `${overall.label} · last poll ${ago(new Date(dataUpdatedAt).toISOString())}`
              : isError ? 'unable to reach /api/system/health' : ''}
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Overall card */}
          <div className={cn('card mb-4 max-w-3xl border', overall.bg)}>
            <div className="flex items-center gap-3">
              <OverallIcon size={28} className={overall.tone} />
              <div className="flex-1">
                <div className={cn('text-lg font-bold', overall.tone)}>{overall.label}</div>
                <div className="text-xs text-text-dim">
                  Up since {new Date(data.startedAt).toLocaleString('en-GB', { hour12: false })}
                  {' '}· {Math.floor(data.uptimeSeconds / 3600)}h{' '}
                  {Math.floor((data.uptimeSeconds % 3600) / 60)}m
                </div>
              </div>
            </div>
          </div>

          {/* Per-component cards */}
          <div className="grid gap-3 max-w-3xl lg:grid-cols-2">
            {data.components.map((c) => (
              <ComponentCard key={c.name} c={c} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ComponentCard({ c }: { c: ComponentHealth }) {
  const cfg = STATUS_CFG[c.status] ?? STATUS_CFG.DOWN;
  const Icon = cfg.icon;
  return (
    <div className="card card-tight">
      <div className="flex items-start gap-3 mb-3">
        <Icon size={18} className={cn('mt-0.5 flex-none', cfg.tone)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-sm">{c.name}</h3>
            <span className={cn(
              'inline-flex items-center px-1.5 py-0.5 rounded-chip text-[10px] font-semibold border',
              cfg.bg, cfg.tone,
            )}>
              {cfg.label}
            </span>
          </div>
          <div className="text-xs text-text-dim">{c.summary}</div>
        </div>
      </div>
      {c.details.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pt-2 border-t border-[color:var(--border)] text-xs">
          {c.details.map((d) => (
            <div key={d.label} className="contents">
              <dt className="text-text-faint">{d.label}</dt>
              <dd className="font-mono text-text-dim truncate" title={d.value}>{d.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
